import { useState, useRef, useEffect } from "react";
import style from "./styles.module.scss";
import nav from "./nav.module.scss";
import offCanvas from "./offCanvas.module.scss";
import { checkCookieDisclaimer } from "../../utils/DisclaimerCookie";
import { Disclaimer } from "./Disclaimer";
import Link from "next/link";
import Logo from "../../public/static-next/logo-finanzero.svg";
import { isLocalStorageAvailable } from "../../utils/localStorageAvailable";
import { NewMenu } from "../../types/Menu";
import ArrowDown from "../../public/static-next/icons/chevron-down-solid.svg";

interface ComponentTypes {
  items: Array<NewMenu>;
}

export const HeaderMenu = ({ items }: ComponentTypes) => {
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);
  const [hamburgerMenuClicked, sethamburgerMenuClicked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<string | null>(null);

  const openCheck = useRef<HTMLDivElement>();
  const mobileOffcanvas = useRef<HTMLDivElement>();
  const subLinksMenu = useRef<HTMLUListElement[]>([]);

  useEffect(() => {
    // Abrindo o menu hamburger, o body não pode ter scroll
    hamburgerMenuClicked
      ? document.body.classList.add("overflow_hidden")
      : document.body.classList.remove("overflow_hidden");
  }, [hamburgerMenuClicked]);

  useEffect(() => {
    if (isLocalStorageAvailable()) {
      const item = localStorage.getItem("currentUser");
      setIsLoggedIn(item);
    }
  }, []);

  useEffect(() => {
    const check = checkCookieDisclaimer("read_cookies_disclaimer", isLoggedIn);
    setShowDisclaimer(check);
  }, [showDisclaimer, isLoggedIn]);

  const isOpen = () => {
    return openCheck.current.classList.contains("has_offcanvas");
  };

  const toggle = () => {
    if (isOpen()) {
      close();
      sethamburgerMenuClicked(false);
    } else {
      open();
      sethamburgerMenuClicked(true);
    }
  };

  const open = () => {
    mobileOffcanvas.current.setAttribute("aria-hidden", "false");
    openCheck.current.classList.add("has_offcanvas", style.has_offcanvas);
  };

  const close = () => {
    mobileOffcanvas.current.setAttribute("aria-hidden", "true");
    openCheck.current.classList.remove("has_offcanvas", style.has_offcanvas);
  };

  const hideShowSubmenu = (index: number) => {
    if (subLinksMenu.current[index].style.display == "block") {
      subLinksMenu.current[index].style.display = "none";
    } else {
      subLinksMenu.current[index].style.display = "block";
    }
  };

  function getMenuLoginButtonText(
    items: NewMenu[],
    item: { label: string },
    index: number
  ) {
    if (items.length === index + 1) {
      if (isLoggedIn) {
        return "Meus pedidos";
      } else {
        return "Entrar";
      }
    } else {
      return item.label;
    }
  }

  return (
    <>
      {!showDisclaimer && <Disclaimer />}

      <header
        ref={openCheck}
        className={[
          style.header,
          style.header__with_background,
          style.openClass,
        ].join(" ")}
      >
        <div className={style.warning}><strong>Atenção:</strong> Não exigimos nenhum depósito ou pagamento para análise ou liberação de crédito.</div>
        <div className={[style.header_row, style.row].join(" ")}>
          <Link href="/" passHref className={style.logo_link} prefetch={false}>
            <Logo title={"Logo FinanZero"} />
          </Link>

          <button
            className={[style.hamburger_menu, style.tappable_button].join(
              " "
            )}
            onClick={() => {
              toggle();
            }}
            aria-label="menu"
            role="button"
          >
            <span className={style.hamburger_menu__inner}>
              <span className={style.hamburger_menu__lines}></span>
            </span>
          </button>

          <nav className={[style.header_nav, style.nav].join(" ")}>
            <ul id="menu-menu-principal" className={nav.nav_list}>
              {items.length > 0 &&
                items.map((item, index) => {
                  return (
                    <li
                      key={item.id}
                      {...(item.children.length
                        ? {
                          className: [
                            nav.nav_item,
                            nav.menu_item_has_children,
                          ].join(" "),
                        }
                        : { className: nav.nav_item })}
                      // verificação no ID, quando chegar no ultimo item (que deverá ser um botão)
                      // adicione o estilo para o botão
                      {...(items.length == index + 1 && {
                        id: nav.nav_item__button,
                      })}
                    >
                      <Link
                        prefetch={false}
                        className={nav.nav_link}
                        href={item.url}
                      >
                        <span>
                          {getMenuLoginButtonText(items, item, index)}
                        </span>
                      </Link>
                      {item.children.length > 0 && (
                        <ul className={nav.sub_menu}>
                          {item.children.map((children) => {
                            return (
                              <li
                                key={children.id}
                                className={[nav.menu_item, nav.nav_item].join(
                                  " "
                                )}
                              >
                                <Link
                                  prefetch={false}
                                  className={nav.nav_link}
                                  href={children.url}
                                >
                                  <span>{children.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
            </ul>
          </nav>
        </div>

        <div
          className={[offCanvas.offcanvas, "offcanvas"].join(" ")}
          ref={mobileOffcanvas}
          aria-hidden="true"
        >
          <div className={offCanvas.offcanvas_scrollable}>
            <nav className={offCanvas.offcanvas_nav}>
              <ul
                id="menu-menu-principal"
                className={offCanvas.offcanvas_nav_list}
              >
                {items.length > 0 &&
                  items.map((item, index) => {
                    return (
                      <li
                        key={item.id}
                        {...(item.children.length
                          ? {
                            className: [
                              nav.nav_item,
                              offCanvas.menu_item_has_children,
                            ].join(" "),
                          }
                          : { className: nav.nav_item })}
                        // verificação no ID, quando chegar no ultimo item (que deverá ser um botão)
                        // adicione o estilo para o botão
                        {...(items.length == index + 1 && {
                          id: offCanvas.nav_item__button,
                        })}
                      >
                        <span
                          className={offCanvas.nav_link}
                          onClick={
                            item.url === "#"
                              ? () => hideShowSubmenu(index)
                              : undefined
                          }
                        >
                          <Link prefetch={false} href={item.url}>
                            {getMenuLoginButtonText(items, item, index)}
                          </Link>
                          {item.children.length ? (
                            <ArrowDown
                              onClick={
                                item.url !== "#"
                                  ? () => hideShowSubmenu(index)
                                  : undefined
                              }
                            />
                          ) : null}
                        </span>
                        {item.children.length > 0 && (
                          <ul
                            ref={(element) => {
                              subLinksMenu.current[index] = element;
                            }}
                            className={offCanvas.sub_menu}
                          >
                            {item.children.map((children) => {
                              return (
                                <li
                                  key={children.id}
                                  className={[offCanvas.nav_item].join(" ")}
                                >
                                  <Link
                                    prefetch={false}
                                    className={offCanvas.nav_link}
                                    href={children.url}
                                  >
                                    <span>{children.label}</span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};
